/**
 * OnboardingTour Component
 * Interactive guided tour for new users
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  CheckCircle,
  X
} from '@phosphor-icons/react';
import { getTourSteps } from '../tours';
import TourVideo from './TourVideo';
import { useTourProgress } from '../hooks/useTourProgress';

// Spotlight overlay component
const Spotlight = ({ targetRect, padding = 8 }) => {
  if (!targetRect) return null;

  const { top, left, width, height } = targetRect;

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none" aria-hidden="true">
      {/* Dark overlay with hole — restrained backdrop */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={left - padding}
              y={top - padding}
              width={width + padding * 2}
              height={height + padding * 2}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(10, 14, 26, 0.72)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Highlight ring — hairline cyan, offset to Onyx bg */}
      <div
        className="absolute rounded-md ring-2 ring-cyan-400/50 ring-offset-4 ring-offset-[#0D1117] animate-pulse"
        style={{
          top: top - padding,
          left: left - padding,
          width: width + padding * 2,
          height: height + padding * 2,
          animationDuration: '2.4s'
        }}
      />
    </div>
  );
};

// Tooltip component
const TourTooltip = ({ step, stepIndex, totalSteps, targetRect, onNext, onPrev, onSkip, onComplete }) => {
  const Icon = step.icon;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const isCenter = step.position === 'center' || !targetRect;

  // Calculate position based on step.position and targetRect
  const getTooltipStyle = () => {
    if (isCenter) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const margin = 16;
    const tooltipWidth = 380;
    const tooltipHeight = 220;

    let style = { position: 'fixed' };

    switch (step.position) {
      case 'bottom':
        style.top = targetRect.bottom + margin;
        style.left = targetRect.left + targetRect.width / 2;
        style.transform = 'translateX(-50%)';
        break;
      case 'bottom-right':
        style.top = Math.min(targetRect.bottom + margin, window.innerHeight - tooltipHeight - margin);
        style.left = Math.min(targetRect.right - tooltipWidth, window.innerWidth - tooltipWidth - margin);
        break;
      case 'bottom-left':
        style.top = targetRect.bottom + margin;
        style.left = Math.max(targetRect.left, margin);
        break;
      case 'right':
        style.top = targetRect.top;
        style.left = targetRect.right + margin;
        break;
      case 'left':
        style.top = targetRect.top;
        style.right = window.innerWidth - targetRect.left + margin;
        break;
      case 'top':
        style.bottom = window.innerHeight - targetRect.top + margin;
        style.left = targetRect.left + targetRect.width / 2;
        style.transform = 'translateX(-50%)';
        break;
      default:
        style.top = '50%';
        style.left = '50%';
        style.transform = 'translate(-50%, -50%)';
    }

    // Ensure tooltip stays within viewport
    if (style.left && typeof style.left === 'number') {
      style.left = Math.max(margin, Math.min(style.left, window.innerWidth - tooltipWidth - margin));
    }
    if (style.top && typeof style.top === 'number') {
      style.top = Math.max(margin, Math.min(style.top, window.innerHeight - tooltipHeight - margin));
    }

    return style;
  };

  const eyebrow = isCenter
    ? (isFirst ? '// GUIDED TOUR' : '// TOUR COMPLETE')
    : `// STEP ${String(stepIndex + 1).padStart(2, '0')} / ${String(totalSteps).padStart(2, '0')}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-step-${step.id}-title`}
      className="fixed z-[9999] w-[380px] bg-slate-900/95 backdrop-blur-xl border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden"
      style={getTooltipStyle()}
    >
      {/* Header — hairline separator, no gradient */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-white/[0.06]">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 mt-0.5 h-9 w-9 rounded-md bg-cyan-500/[0.08] border border-cyan-500/[0.12] flex items-center justify-center">
            <Icon className="h-4.5 w-4.5 text-cyan-400" weight="regular" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400/90">
              {eyebrow}
            </div>
            <h3
              id={`tour-step-${step.id}-title`}
              className="mt-1 text-[15px] font-semibold tracking-tight text-slate-100 truncate"
            >
              {step.title}
            </h3>
          </div>
        </div>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Cerrar tour"
          className="shrink-0 p-1.5 text-slate-500 hover:text-slate-200 rounded-md hover:bg-v4-panel/[0.06] transition-colors"
        >
          <X className="h-4 w-4" weight="regular" aria-hidden="true" />
        </button>
      </div>

      {/* Content */}
      <div className="px-5 py-4">
        {step.videoFallback && document.querySelector('[data-empty="true"]') ? (
          <TourVideo
            src={step.videoFallback.src}
            poster={step.videoFallback.poster}
          />
        ) : (
          <p className="text-[13px] text-slate-300 leading-relaxed">
            {step.description}
          </p>
        )}
      </div>

      {/* Progress dots */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-1.5" role="progressbar" aria-valuenow={stepIndex + 1} aria-valuemin={1} aria-valuemax={totalSteps}>
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <span
              key={idx}
              aria-hidden="true"
              className={`h-1 rounded-full transition-all duration-300 ${
                idx === stepIndex
                  ? 'w-6 bg-cyan-400'
                  : idx < stepIndex
                    ? 'w-1.5 bg-cyan-500/40'
                    : 'w-1.5 bg-slate-700'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06] bg-slate-950/40">
        <button
          type="button"
          onClick={onSkip}
          className="text-[12px] font-medium text-slate-400 hover:text-slate-200 transition-colors"
        >
          Saltar tour
        </button>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              type="button"
              onClick={onPrev}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-slate-300 border border-white/[0.08] hover:border-cyan-500/[0.2] hover:bg-cyan-500/[0.04] hover:text-slate-100 rounded-md transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Anterior
            </button>
          )}
          {isLast ? (
            <button
              type="button"
              onClick={onComplete}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[12px] font-semibold text-slate-950 bg-cyan-500 hover:bg-cyan-400 rounded-md transition-colors"
            >
              <CheckCircle className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
              Finalizar
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1 px-3.5 py-1.5 text-[12px] font-semibold text-slate-950 bg-cyan-500 hover:bg-cyan-400 rounded-md transition-colors"
            >
              Siguiente
              <ChevronRight className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main OnboardingTour component
const OnboardingTour = ({ isOpen, onClose, forceShow = false, mode = 'onboarding', length = 'quick' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [stepsSeen, setStepsSeen] = useState([]);
  const { recordStep } = useTourProgress();

  const tourSteps = useMemo(() => getTourSteps({ mode, length }), [mode, length]);

  // Find and highlight target element
  const updateTargetRect = useCallback(() => {
    const step = tourSteps[currentStep];
    if (!step.highlight) {
      setTargetRect(null);
      return;
    }

    let element = document.querySelector(step.highlight);
    if (!element && step.fallbackSelector) {
      element = document.querySelector(step.fallbackSelector);
    }

    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStep, tourSteps]);

  useEffect(() => {
    if (isOpen) {
      updateTargetRect();
      window.addEventListener('resize', updateTargetRect);
      window.addEventListener('scroll', updateTargetRect);

      return () => {
        window.removeEventListener('resize', updateTargetRect);
        window.removeEventListener('scroll', updateTargetRect);
      };
    }
  }, [isOpen, updateTargetRect]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextIndex = currentStep + 1;
      const currentId = tourSteps[currentStep]?.id ?? String(currentStep);
      const nextId = tourSteps[nextIndex]?.id ?? String(nextIndex);
      const newSeen = [...new Set([...stepsSeen, currentId, nextId])];
      setStepsSeen(newSeen);
      try { recordStep(mode, length, nextId, newSeen); } catch (_) { /* silencio-ok: telemetría del tour, el usuario no pierde nada */ }
      setCurrentStep(nextIndex);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('tourCompleted', 'true');
    localStorage.setItem('tourSkipped', 'true');
    setCurrentStep(0);
    onClose();
  };

  const handleComplete = () => {
    localStorage.setItem('tourCompleted', 'true');
    localStorage.setItem('tourCompletedAt', new Date().toISOString());
    const finalId = tourSteps[currentStep]?.id ?? String(currentStep);
    const allSeen = [...new Set([...stepsSeen, finalId])];
    try { recordStep(mode, length, 'complete', allSeen); } catch (_) { /* silencio-ok: telemetría del tour, el usuario no pierde nada */ }
    setStepsSeen([]);
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen) return null;

  const step = tourSteps[currentStep];

  return createPortal(
    <>
      {/* Spotlight overlay */}
      <Spotlight targetRect={targetRect} />

      {/* Tooltip */}
      <TourTooltip
        step={step}
        stepIndex={currentStep}
        totalSteps={tourSteps.length}
        targetRect={targetRect}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleSkip}
        onComplete={handleComplete}
      />
    </>,
    document.body
  );
};

// Hook to manage tour state.
// startTour acepta opcionalmente { mode, length } para configurar el tour antes de abrir.
export const useTour = () => {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourMode, setTourMode] = useState('onboarding');
  const [tourLength, setTourLength] = useState('quick');

  const startTour = useCallback(({ mode, length } = {}) => {
    if (mode) setTourMode(mode);
    if (length) setTourLength(length);
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem('tourCompleted');
    localStorage.removeItem('tourSkipped');
    localStorage.removeItem('tourCompletedAt');
  }, []);

  const shouldShowTour = useCallback(() => {
    return !localStorage.getItem('tourCompleted');
  }, []);

  return {
    isTourOpen,
    tourMode,
    tourLength,
    startTour,
    closeTour,
    resetTour,
    shouldShowTour
  };
};

export default OnboardingTour;
